import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

const IDLE_MS = 8000;
const ROTATE_MS = 6000;

const offers = [
  {
    label: "Leadership Assessment",
    aria: "Leadership Gaps Assessment, free",
    to: "/leadership-gaps-assessment/",
  },
  {
    label: "Expectations Builder",
    aria: "Expectations Builder, free",
    href: "https://free.expectation.builder.leadershipedge.live/",
  },
] as const;

/** Vertical floating CTA pinned to the right edge on desktop. Rotates offers; hides after 8s idle. Mobile uses the hamburger menu. */
export function FloatingAssessmentCTA() {
  const [hidden, setHidden] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setHidden(true), IDLE_MS);
    };

    const onActivity = () => {
      setHidden(false);
      schedule();
    };

    const events = ["mousemove", "scroll", "keydown", "touchstart"] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    schedule();

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % offers.length), ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  const offer = offers[index];

  const className = `fixed right-0 top-1/2 z-40 hidden -translate-y-1/2 items-center gap-2 rounded-l-[8px] bg-[#693D94] px-2.5 py-4 text-sm font-semibold uppercase tracking-wide text-white shadow-brand transition-transform duration-500 lg:flex ${
    hidden ? "translate-x-full" : "translate-x-0 hover:-translate-x-1"
  }`;
  const style = { fontFamily: "'Poppins', sans-serif", writingMode: "vertical-rl" } as const;

  const inner = (
    <>
      <span className="mt-2 rounded-full bg-white px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase leading-none tracking-wider text-[#693D94]">
        FREE
      </span>
      {/* Grid stack keeps the ribbon a fixed size: the longest label reserves the space. */}
      <span className="mb-2 grid">
        <span className="col-start-1 row-start-1 uppercase">{offer.label}</span>
        {offers.map((o) => (
          <span
            key={o.label}
            aria-hidden="true"
            className="col-start-1 row-start-1 invisible uppercase"
          >
            {o.label}
          </span>
        ))}
      </span>
    </>
  );

  if ("href" in offer) {
    return (
      <a
        href={offer.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={offer.aria}
        data-idle-hidden={hidden ? "true" : "false"}
        className={className}
        style={style}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link
      to={offer.to}
      aria-label={offer.aria}
      data-idle-hidden={hidden ? "true" : "false"}
      className={className}
      style={style}
    >
      {inner}
    </Link>
  );
}
